import {
  Account,
  AccountCategory,
  Currency,
  Keys,
  OrderResponseMessage,
  OrderType,
  ResponseMessageType,
  TradeType,
  useDictionary,
} from '@eih-netbroker/common';
import { useCallback } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { useEStream } from '../../../../shared/services/sse/useEStream';
import { useAccountBalance } from '../../../../shared/services/useAccountBalance';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { useTradingClassification } from '../../../../shared/services/useTradingClassification';
import { ValidatedOrder } from './useNormalOrderView';

export const useOrderClientValidation = () => {
  const { getAccountById } = useAccounts();
  const { translation, translateWithParams } = useLocale();
  const { formatDictionaryType } = useDictionary();
  const { tradingClassification } = useTradingClassification();
  const { getCashInfoForCurrency } = useAccountBalance();
  const { loadInstrumentSingle } = useEStream();

  const validateAccounts = useCallback(
    (order: ValidatedOrder, account: Account) => {
      const messages: OrderResponseMessage[] = [];

      if (account.accountCategory === AccountCategory.Nyesz && !order.instrument.allowToNYESZ) {
        messages.push({
          msg: translateWithParams(translation.orderModal.preValidateWarnings.NYESZNotAllow, {
            account: formatDictionaryType(account.accountCategory, 'accountCategoryTypes'),
          }),
          msgType: ResponseMessageType.Error,
        });
      }

      return messages;
    },
    [translateWithParams, translation, formatDictionaryType],
  );

  // In version 1.0.0, we don't validate short trades -- [NN-2729]
  // const validateShort = useCallback(
  //   (order: ValidatedOrder, accountId: string) => {
  //     const messages: OrderResponseMessage[] = [];

  //     const inventoryCount = getInventoryCount(order.instrument.ric, accountId);
  //     if (inventoryCount < order.quantity && order.tradeType === TradeType.Sell) {
  //       if (!order.instrument.canBeShorted) {
  //         messages.push({
  //           msg: translation.orderModal.preValidateWarnings.instrumentNotShortable,
  //           msgType: ResponseMessageType.Error,
  //         });
  //       }

  //       if (!tradingClassification?.allowShortTrade) {
  //         messages.push({
  //           msg: translation.orderModal.preValidateWarnings.allowShortTradeMissing,
  //           msgType: ResponseMessageType.Error,
  //         });
  //       }
  //     }

  //     return messages;
  //   },
  //   [getInventoryCount, translation, tradingClassification],
  // );

  const getInvestmentLoanPrice = useCallback(
    async (order: ValidatedOrder) => {
      if (order.orderType === OrderType.Market || order.orderType === OrderType.StopMarket) {
        return (await loadInstrumentSingle(order.instrument)).price?.value;
      } else {
        return order.price;
      }
    },
    [loadInstrumentSingle],
  );

  const _validateInvestmentLoan = useCallback(
    async (order: ValidatedOrder, account: Account) => {
      const messages: OrderResponseMessage[] = [];
      try {
        if (order.tradeType === TradeType.Buy) {
          const cashInfo = getCashInfoForCurrency(
            {
              account: account,
              client: undefined,
            },
            order.instrument.currency as Currency,
          );

          const price = await getInvestmentLoanPrice(order);

          if (cashInfo.isLoaded && price) {
            if (cashInfo.cash < order.quantity * price) {
              if (!tradingClassification?.hasInvestmentLoanContract) {
                messages.push({
                  msg: translation.orderModal.preValidateWarnings.insufficientCash,
                  msgType: ResponseMessageType.Error,
                });
              }
            }
          }
        }
      } catch (err) {
        // if we can't get the price, we can't validate the cash
      }

      return messages;
    },
    [getCashInfoForCurrency, getInvestmentLoanPrice, translation, tradingClassification],
  );

  const validateBuxFuture = useCallback(
    async (order: ValidatedOrder) => {
      const messages: OrderResponseMessage[] = [];

      if (order.instrument.ric === Keys.Instruments.Ric.BUXZ3 && !tradingClassification?.allowBuxFutureTrade) {
        messages.push({
          msg: translation.orderModal.preValidateWarnings.buxFutureTradeMissing,
          msgType: ResponseMessageType.Error,
        });
      }

      return messages;
    },
    [translation, tradingClassification],
  );

  const validateBeforeCreateOrder = useCallback(
    async (order: ValidatedOrder, accountId: string) => {
      const messages: OrderResponseMessage[] = [];
      const account = getAccountById(accountId);

      // account validations
      messages.push(...validateAccounts(order, account));

      // short validations
      // messages.push(...validateShort(order, accountId));

      // investment load validations
      // messages.push(...(await validateInvestmentLoan(order, account)));

      // bux futures validations
      validateBuxFuture(order);

      return messages;
    },
    [getAccountById, validateAccounts, validateBuxFuture],
  );

  return { validateBeforeCreateOrder };
};
